<script setup lang="ts">
import type { DistributionGroup } from '@forgd/contract/types'
import type { ColumnDef } from '@forgd/ui-kit/blocks/data-table'
import { useNumberFieldBlurGuard } from '#design-tokenomics/composables/useNumberFieldBlurGuard'
import { ColumnHeader, DataTable, InputCell } from '@forgd/ui-kit/blocks/data-table'
import { Button } from '@forgd/ui-kit/button'
import { Input } from '@forgd/ui-kit/input'
import { InputGroup, InputGroupAddon } from '@forgd/ui-kit/input-group'
import { NumberField, NumberFieldInput } from '@forgd/ui-kit/number-field'
import { Select, SelectContent, SelectItem, SelectItemText, SelectTrigger, SelectValue } from '@forgd/ui-kit/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@forgd/ui-kit/tooltip'
import { TrashIcon } from '@heroicons/vue/24/outline'

const emit = defineEmits<{
  deleteGroup: [id: string]
}>()

const model = defineModel<Array<Partial<DistributionGroup>>>()

function commit() {
  model.value = [...(model.value ?? [])]
}

const types = [
  {
    id: 'internal',
    label: 'Internal',
    description: 'Insiders: Team, Investors, Advisors, etc.',
  },
  {
    id: 'external',
    label: 'External',
    description: 'Outsiders: Rewards, Airdrops, etc.',
  },
] as const

const categories = [
  {
    id: 'Core Contributors',
    label: 'Core Contributors',
    description: 'Team, Advisors, Employees, etc.',
  },
  {
    id: 'Private Sale',
    label: 'Private Investors',
    description: 'Seed, Private, Strategic Fundraise Participants',
  },
  {
    id: 'Public Sale',
    label: 'Public Sale',
    description: 'Public Sale Participants',
  },
  {
    id: 'Ecosystem Incentives',
    label: 'Ecosystem Incentives',
    description: 'Token yield, Grants, Incentivised Testnet, Governance',
  },
  {
    id: 'Liquidity Provision',
    label: 'Liquidity Provision',
    description: 'Market maker loans or trading capital used to seed DEX pools',
  },
  {
    id: 'Airdrops',
    label: 'Airdrops',
    description: 'Airdrops',
  },
  {
    id: 'Marketing',
    label: 'Marketing',
    description: 'Marketing, KOL, Exchange Partnerships',
  },
  {
    id: 'Treasury',
    label: 'Treasury',
    description: 'Treasury, DAO Treasury, Protocol Development',
  },
  {
    id: 'Miscellaneous',
    label: 'Miscellaneous',
    description: 'Any other smaller position',
  },
] as const

const allocationFormat = { maximumFractionDigits: 2 }
const allocationGuard = useNumberFieldBlurGuard(allocationFormat)

function fixAllocationPercentage(value: number) {
  if (value > 100) {
    return 100
  }
  else if (value < 0) {
    return 0
  }
  else {
    return value
  }
}

const columns: ColumnDef<Partial<DistributionGroup>>[] = [
  {
    accessorKey: 'name',
    size: 200,
    maxSize: 200,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Group Name', class: 'pr-1 pl-2' }),
    cell: ({ row, column }) => h(InputCell, { column }, () => h(Input, {
      'modelValue': row.original.name,
      'onUpdate:modelValue': (v: string | number | null | undefined) => {
        row.original.name = v?.toString() ?? ''
        commit()
      },
      'name': 'name',
      'size': 'sm',
      'aria-label': 'Group Name',
    })),
  },
  {
    accessorKey: 'allocationPercentage',
    size: 100,
    maxSize: 100,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Allocation %', class: 'px-1' }),
    cell: ({ row, column }) => h(InputCell, { column }, () => h(NumberField, {
      'modelValue': row.original.allocationPercentage,
      'onUpdate:modelValue': (v: number | undefined) => {
        if (!allocationGuard.accepts(v, row.original.allocationPercentage)) {
          return
        }
        row.original.allocationPercentage = Number(v) || 0
        commit()
      },
      'name': 'allocationPercentage',
      'min': 0,
      'max': 100,
      'formatOptions': allocationFormat,
      'stepSnapping': false,
    }, () => h(InputGroup, {
      size: 'sm',
      error: (row.original.allocationPercentage ?? 0) < 0 || (row.original.allocationPercentage ?? 0) > 100,
    }, () => [
      h(NumberFieldInput, {
        ...allocationGuard.inputListeners,
        'aria-label': 'Allocation %',
        'onBlur': () => {
          row.original.allocationPercentage = fixAllocationPercentage(row.original.allocationPercentage ?? 0)
          commit()
        },
      }),
      h(InputGroupAddon, null, () => '%'),
    ]))),
  },
  {
    accessorKey: 'type',
    size: 100,
    maxSize: 100,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Type', class: 'px-1', tooltip: 'Type: Categorise your groups to internal and external. It will help you keep an overview of potential selling groups (Insiders: Team, Investors, Advisors, etc.) and balance them out between externals.' }),
    cell: ({ row, column }) => h(InputCell, { column }, () => h(Select<typeof types[number]['id']>, {
      'modelValue': row.original.type,
      'onUpdate:modelValue': (value) => {
        row.original.type = value
        commit()
      },
    }, () => [
      h(SelectTrigger, { size: 'sm' }, () => h(SelectValue)),
      h(SelectContent, { class: 'max-w-50' }, () => types.map(opt =>
        h(SelectItem, { value: opt.id, composed: true }, () => h('div', { class: 'flex flex-1 flex-col' }, [
          h(SelectItemText, { class: 'font-semibold text-primal' }, () => opt.label),
          h('span', { class: 'text-xs text-wrap text-gray-600' }, opt.description),
        ])),
      )),
    ])),
  },
  {
    accessorKey: 'category',
    size: 130,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Group Category', class: 'px-1', tooltip: 'Group Category: Use our predefined categories to label your groups. This will allow you to present a cleaned-up version of your distribution for specific audiences, such as your community.' }),
    cell: ({ row, column }) => h(InputCell, { column }, () => h(Select<typeof categories[number]['id']>, {
      'modelValue': row.original.category,
      'onUpdate:modelValue': (value) => {
        row.original.category = value
        commit()
      },
    }, () => [
      h(SelectTrigger, { size: 'sm' }, () => h(SelectValue)),
      h(SelectContent, { class: 'max-w-55' }, () => categories.map(opt =>
        h(SelectItem, { value: opt.id, composed: true }, () => h('div', { class: 'flex flex-1 flex-col' }, [
          h(SelectItemText, { class: 'font-semibold text-primal' }, () => opt.label),
          h('span', { class: 'text-xs text-wrap text-gray-600' }, opt.description),
        ])),
      )),
    ])),
  },
  {
    id: 'delete',
    size: 44,
    maxSize: 44,
    header: '',
    cell: ({ row, column }) => h(InputCell, { column }, () => h(Tooltip, () => [
      h(TooltipTrigger, { asChild: true }, () => h(Button, {
        'variant': 'outline',
        'size': 'sm',
        'iconOnly': true,
        'aria-label': 'Delete group',
        'onClick': () => row.original.id && emit('deleteGroup', row.original.id),
      }, () => h(TrashIcon, { 'aria-hidden': 'true' }))),
      h(TooltipContent, () => 'Delete group'),
    ])),
  },
]
</script>

<template>
  <div class="min-h-94.25" data-testid="DtDistributionTable">
    <DataTable :columns="columns" :data="model ?? []" size="md" />
  </div>
</template>
